import React, { useEffect } from 'react';
import Icon from '@ant-design/icons/lib/components/Icon';
import { theme } from '../theme/semanticTokens';
import {
  IconProps,
  IconSize,
  IconVariant,
  SVGComponent
} from '../types/icons/Icon.types';
import { iconPathMapping } from '../types/icons/IconMap';
import { IconType } from '../types/icons/IconTypes.enum';

const importIcon = (size: IconSize, type: IconType, variant: IconVariant) => {
  try {
    const iconComponent = iconPathMapping[type]?.[variant]?.[size];
    if (!iconComponent)
      throw new Error(
        `Icon ${type} not found in size ${size} and variant ${variant}.`
      );
    return iconComponent;
  } catch (e) {
    console.error(
      `Error loading icon ${type} in size ${size} and variant ${variant}:`,
      e
    );
    return null;
  }
};

export const IconWrap: React.FC<React.PropsWithChildren<IconProps>> = ({
  type,
  size = IconSize.Medium,
  variant = IconVariant.Outline,
  disabled = false,
  style,
  ...props
}: IconProps) => {
  const [Component, setComponent] = React.useState<SVGComponent | null>(null);

  useEffect(() => {
    const loadIcon = () => {
      const iconComponent = importIcon(size, type, variant);
      setComponent(() => iconComponent);
    };
    loadIcon();
  }, [type, size, variant]);

  if (!Component) {
    return null;
  }

  const defaultStyle: React.CSSProperties = {
    fontSize: size,
    ...(disabled && {
      color: theme.dime.colors.icon.disabled,
      cursor: 'not-allowed',
      pointerEvents: 'none'
    }),
    ...style
  };

  return (
    <Icon
      component={Component as SVGComponent}
      style={defaultStyle}
      {...props}
    />
  );
};
